import React, { useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';

import TripAnomaliesAPI from '../../api/trip-anomalies';
import ErrorBoundary from '../../components/ErrorBoundary';
import SpaceSpinner from '../../components/SpaceSpinner';
import TripsTable from '../../components/Table/TripsTable';
import Text from '../../components/Text';
import TextInput from '../../components/TextInput';
import { getCenterCoordinates } from '../../utils/map';
import { getActiveRoutes } from '../../views/trips/trips-page-helpers';
import TripComparisonSection from './TripComparisonSection';
import { useTripAnomalyData } from './useTripAnomalyData';

const ExpandedTripAnomaly = ({
  tripAnomalyId,
  t,
  openAnomalyDetailModalVisible,
  setSelectedMapConfig,
}) => {
  const tripAnomalyDetailsQuery = useQuery({
    enabled: !!tripAnomalyId,
    staleTime: Infinity,
    queryKey: ['fetchTripAnomalyDetails', tripAnomalyId],
    queryFn: () => new TripAnomaliesAPI().fetchTripAnomalyDetails(tripAnomalyId),
  });

  const {
    trips,
    tripData,
    distanceAndCoordinatesAdjustment,
    note,
    hasTripData,
    hasAdjustmentData,
    hasOverlappingTrips,
    hasNote,
    journeyTimestamps,
  } = useTripAnomalyData(tripAnomalyDetailsQuery.data);

  const combinedRoutes = useMemo(() => {
    const routes = [];

    if (hasTripData && tripData?.journeyGeoJson) {
      const recordedRoutes = getActiveRoutes({ journeyGeoJson: tripData.journeyGeoJson });
      if (recordedRoutes.length > 0) {
        routes.push({
          ...recordedRoutes[0],
          color: '#6b99f0',
        });
      }
    }

    if (hasAdjustmentData && distanceAndCoordinatesAdjustment?.journeyGeoJson) {
      const adjustedRoutes = getActiveRoutes({
        journeyGeoJson: distanceAndCoordinatesAdjustment.journeyGeoJson,
      });
      if (adjustedRoutes.length > 0) {
        routes.push({
          ...adjustedRoutes[0],
          color: '#ff8c42',
        });
      }
    }

    return routes;
  }, [hasTripData, hasAdjustmentData, tripData, distanceAndCoordinatesAdjustment]);

  const combinedMapCenter = useMemo(() => {
    if (combinedRoutes.length === 0) return undefined;

    const allCoordinates = [];
    combinedRoutes.forEach(route => {
      if (route.polyline && route.polyline.length > 0) {
        route.polyline.forEach(point => {
          allCoordinates.push([point.lng, point.lat]);
        });
      }
    });

    if (allCoordinates.length === 0) return undefined;

    const centerConfig = getCenterCoordinates(allCoordinates);
    return centerConfig.center;
  }, [combinedRoutes]);

  const handleMapClick = useCallback(
    (trp, activeRoutes, mapCenter, tripType) => {
      const initialRouteVisibility = tripType
        ? {
            recorded: tripType === 'recorded',
            adjusted: tripType === 'adjusted',
          }
        : { recorded: true, adjusted: true };

      setSelectedMapConfig({
        routes: combinedRoutes.length > 0 ? combinedRoutes : activeRoutes,
        center: combinedMapCenter || mapCenter,
        initialRouteVisibility,
      });
      openAnomalyDetailModalVisible();
    },
    [setSelectedMapConfig, openAnomalyDetailModalVisible, combinedRoutes, combinedMapCenter],
  );

  const handleTripsTableFullScreen = useCallback(
    (trp, activeRoutes, mapCenter) => {
      setSelectedMapConfig({
        routes: activeRoutes,
        center: mapCenter,
      });
      openAnomalyDetailModalVisible();
    },
    [setSelectedMapConfig, openAnomalyDetailModalVisible],
  );

  if (tripAnomalyDetailsQuery.isFetching) return <SpaceSpinner />;

  return (
    <div>
      <ErrorBoundary>
        <TripComparisonSection
          hasTripData={hasTripData}
          hasAdjustmentData={hasAdjustmentData}
          tripData={tripData}
          distanceAndCoordinatesAdjustment={distanceAndCoordinatesAdjustment}
          tripAnomalyDetailsQuery={tripAnomalyDetailsQuery}
          journeyTimestamps={journeyTimestamps}
          t={t}
          onMapClick={handleMapClick}
        />
      </ErrorBoundary>

      {hasNote && (
        <div style={{ width: '100%' }}>
          <Text variant="b">{t('reviewNote')}</Text>
          <TextInput textArea value={note} disabled={true} autoSize={{ minRows: 2, maxRows: 6 }} />
        </div>
      )}

      {hasOverlappingTrips && (
        <div style={{ width: '100%', marginTop: '32px' }}>
          <Text variant="b">{t('overlappingTrips')}</Text>
          <TripsTable
            t={t}
            showSystemAdminColumns={true}
            showSearchInput={false}
            loading={tripAnomalyDetailsQuery.isFetching}
            dataSource={trips}
            handleViewFullScreen={handleTripsTableFullScreen}
          />
        </div>
      )}
    </div>
  );
};

export default React.memo(ExpandedTripAnomaly);
